/* EDI Snow Report engine (12 Oct 2026). One file, two homes: the website section
   (sections/edi-snow-report.liquid) and Max's personal dashboard (dashboard/snow-report.html).
   build.py pastes this file into both, so edit it here and rebuild.

   Two data sources, one model, one set of cards:
   - Open-Meteo (personal dashboard; free tier is non-commercial). ONE request for every mountain at its
     top-lift elevation: 6 daily + 3 hourly variables, 7 days back and 7 ahead. Has snowfall, history
     and the freezing level.            -> parse()
   - MET Norway Locationforecast (the website; CC BY 4.0, commercial use allowed). One request per
     mountain at its top-lift altitude. Forecast only (~9 days), no snowfall field: snow is estimated
     from precipitation and the temperature at the top.            -> parseMet()
   Numbers are model output for that elevation, not the resort's own report, and the page says so.
   - Avalanche Canada (backcountry block that leads the Canada group): public API, one product per point,
     always 3 days of danger ratings.            -> avyUrl() / parseAvy()

   Record: endeavor/ecom/theme/snow-report-2026-10-12/README.md */
(function (global) {
  'use strict';

  var DAILY = ['weather_code', 'temperature_2m_max', 'temperature_2m_min', 'snowfall_sum', 'precipitation_sum', 'wind_gusts_10m_max'];
  var HOURLY = ['snowfall', 'freezing_level_height', 'temperature_2m'];
  var SNOW_CM_PER_MM = 0.7;          // Open-Meteo's own conversion (1 mm of water = 0.7 cm of snow); MET estimates use the same
  var STRIP_CAP_CM = 30;             // a 30 cm day fills the bar; bigger days are labelled
  var FLAGS = { storm72: 20, fresh24: 10, rainMm: 2 };
  var GROUP_NAMES = { CA: 'Canada', US: 'USA', EU: 'Europe', JP: 'Japan' };
  var GROUP_ORDER = ['CA', 'US', 'EU', 'JP'];
  var RANK_SHOWN = 10;                // ranked views show the top 10; the rest sit in one dropdown
  var HOUR = 3600000;

  /* ---------- shared helpers ---------- */

  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function num(v) { return isNum(v) ? v : 0; }
  function round1(v) { return Math.round(v * 10) / 10; }

  function sumRange(arr, from, to) {         // [from, to), clamped, nulls count as 0
    var s = 0;
    for (var i = Math.max(0, from); i < Math.min(arr.length, to); i++) s += num(arr[i]);
    return round1(s);
  }

  function finish(m) {                       // rain day and flags, the same for both sources
    var rainDay = null;
    m.days.filter(function (d) { return !d.past; }).slice(0, 3).forEach(function (x) {
      if (!rainDay && x.rain >= FLAGS.rainMm) rainDay = x;
    });
    m.rainDay = rainDay;
    m.flags = {
      storm: m.next72 >= FLAGS.storm72,
      fresh: m.hasHistory && m.last24 >= FLAGS.fresh24,
      rain: !!rainDay
    };
    return m;
  }

  /* ---------- Open-Meteo ---------- */

  function buildUrl(resorts, opts) {
    opts = opts || {};
    var host = opts.apikey ? 'https://customer-api.open-meteo.com' : 'https://api.open-meteo.com';
    var q = [
      'latitude=' + resorts.map(function (r) { return r.lat; }).join(','),
      'longitude=' + resorts.map(function (r) { return r.lon; }).join(','),
      'elevation=' + resorts.map(function (r) { return r.top; }).join(','),
      'daily=' + DAILY.join(','),
      'hourly=' + HOURLY.join(','),
      'past_days=7', 'forecast_days=7',          // daily: 7 days back, today + 6 ahead
      'past_hours=24', 'forecast_hours=80',       // hourly: the last 24 h, the next 72 h plus 8 h of slack for a cached copy
      'timezone=auto', 'timeformat=unixtime', 'wind_speed_unit=kmh'
    ];
    if (opts.apikey) q.push('apikey=' + encodeURIComponent(opts.apikey));
    return host + '/v1/forecast?' + q.join('&');
  }

  function localIsoHour(nowMs, offsetSec) {  // "2026-10-12T11:00" in the resort's own clock
    return new Date(nowMs + offsetSec * 1000).toISOString().slice(0, 13) + ':00';
  }

  function lastIndexAtOrBefore(times, iso) {
    var k = -1;
    for (var i = 0; i < times.length; i++) { if (times[i] <= iso) k = i; else break; }
    return k;
  }

  // unixtime -> the resort's own wall-clock ISO ("2026-10-12T11:00"); ISO strings pass through
  function localTimes(times, offsetSec, len) {
    return times.map(function (t) {
      return typeof t === 'number' ? new Date((t + offsetSec) * 1000).toISOString().slice(0, len) : t;
    });
  }

  function parse(json, resorts, nowMs) {
    var locs = Array.isArray(json) ? json : [json];
    if (locs.length !== resorts.length) {
      throw new Error('Open-Meteo returned ' + locs.length + ' locations for ' + resorts.length + ' mountains');
    }
    return resorts.map(function (r, i) {
      var d = locs[i], off = d.utc_offset_seconds || 0;
      var h = Object.assign({}, d.hourly, { time: localTimes(d.hourly.time, off, 16) });
      var dy = Object.assign({}, d.daily, { time: localTimes(d.daily.time, off, 10) });
      var nowH = localIsoHour(nowMs, off);
      var hi = Math.max(0, lastIndexAtOrBefore(h.time, nowH));
      var ti = dy.time.indexOf(nowH.slice(0, 10));
      if (ti < 0) ti = Math.min(7, dy.time.length - 1);

      var days = dy.time.map(function (t, k) {
        var snow = num(dy.snowfall_sum[k]);
        var rain = Math.max(0, num(dy.precipitation_sum[k]) - snow / SNOW_CM_PER_MM);
        return {
          date: t, snow: round1(snow), rain: round1(rain),
          tmax: dy.temperature_2m_max[k], tmin: dy.temperature_2m_min[k],
          wind: dy.wind_gusts_10m_max[k], sky: sky(dy.weather_code[k]),
          past: k < ti, today: k === ti
        };
      });

      var hours = [], ticks = [];
      for (var k = hi + 1, j = 0; k <= hi + 72 && k < h.time.length; k++, j++) {
        var sn = num(h.snowfall[k]);
        hours.push({ x0: j / 72, x1: (j + 1) / 72, snow: sn, rate: sn, frz: h.freezing_level_height[k], temp: h.temperature_2m[k] });
        if (h.time[k].slice(11, 13) === '00') ticks.push({ x: j / 72, label: dateParts(h.time[k].slice(0, 10)).dow });
      }
      var frz = hours.map(function (x) { return x.frz; }).filter(isNum);

      return finish({
        resort: r, src: 'open-meteo', hasHistory: true, hasFrz: true, windLabel: 'Gusts',
        tz: d.timezone, elevation: d.elevation,
        last24: sumRange(h.snowfall, hi - 23, hi + 1),
        next24: sumRange(h.snowfall, hi + 1, hi + 25),
        next72: sumRange(h.snowfall, hi + 1, hi + 73),
        last7: sumRange(dy.snowfall_sum, ti - 7, ti),
        next7: sumRange(dy.snowfall_sum, ti, ti + 7),
        nowTemp: h.temperature_2m[hi],
        nowFrz: h.freezing_level_height[hi],
        frzMin: frz.length ? Math.min.apply(null, frz) : null,
        frzMax: frz.length ? Math.max.apply(null, frz) : null,
        today: days[ti],
        days: days.slice(Math.max(0, ti - 7), ti + 7),
        hours: hours, ticks: ticks
      });
    });
  }

  /* ---------- MET Norway ---------- */

  function r4(v) { return String(Math.round(parseFloat(v) * 1e4) / 1e4); }   // MET: at most 4 decimals

  function metUrl(r) {
    return 'https://api.met.no/weatherapi/locationforecast/2.0/complete?lat=' + r4(r.lat) + '&lon=' + r4(r.lon) +
      '&altitude=' + Math.round(parseFloat(r.top));
  }

  // keep only what parseMet reads (a full reply is ~40 KB, this is ~8 KB), so a cached copy stays small
  function trimMet(j) {
    return { properties: { timeseries: j.properties.timeseries.map(function (e) {
      var d = e.data, o = { instant: { details: {
        air_temperature: d.instant.details.air_temperature, wind_speed: d.instant.details.wind_speed } } };
      if (d.next_1_hours) o.next_1_hours = d.next_1_hours;
      if (d.next_6_hours) o.next_6_hours = d.next_6_hours;
      return { time: e.time, data: o };
    }) } };
  }

  // Share of precipitation that falls as snow at the top: all of it at 0 °C or colder, none at 2 °C or warmer.
  function snowShare(t) { return !isNum(t) ? 0 : t <= 0 ? 1 : t >= 2 ? 0 : 1 - t / 2; }

  var fmtCache = {};
  function localParts(ms, tz) {              // {date:'2026-10-12', hour:'07'} in the mountain's clock
    var key = tz || 'UTC', f = fmtCache[key];
    if (!f) {
      try {
        f = new Intl.DateTimeFormat('en-CA', { timeZone: key, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
      } catch (e) {
        f = new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
      }
      fmtCache[key] = f;
    }
    var o = {};
    f.formatToParts(new Date(ms)).forEach(function (p) { o[p.type] = p.value; });
    return { date: o.year + '-' + o.month + '-' + o.day, hour: o.hour === '24' ? '00' : o.hour };
  }

  function addDays(iso, n) {
    var p = iso.split('-'), d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2] + n));
    return d.toISOString().slice(0, 10);
  }

  function metIntervals(ts) {
    var iv = [];
    for (var k = 0; k < ts.length; k++) {
      var e = ts[k], d = e.data, t = Date.parse(e.time);
      var nx = ts[k + 1] ? Date.parse(ts[k + 1].time) : null;
      var inst = (d.instant && d.instant.details) || {};
      var x;
      if (d.next_1_hours && (nx === null || nx - t === HOUR)) {
        x = { s: t, e: t + HOUR, p: num(d.next_1_hours.details && d.next_1_hours.details.precipitation_amount),
              sym: d.next_1_hours.summary && d.next_1_hours.summary.symbol_code,
              t: inst.air_temperature, tmin: inst.air_temperature, tmax: inst.air_temperature, wind: inst.wind_speed };
      } else if (d.next_6_hours) {
        var n6 = d.next_6_hours.details || {}, end = t + 6 * HOUR;
        var both = isNum(n6.air_temperature_max) && isNum(n6.air_temperature_min);
        x = { s: t, e: end, p: num(n6.precipitation_amount), sym: d.next_6_hours.summary && d.next_6_hours.summary.symbol_code,
              t: both ? (n6.air_temperature_max + n6.air_temperature_min) / 2 : inst.air_temperature,
              tmin: both ? n6.air_temperature_min : inst.air_temperature, tmax: both ? n6.air_temperature_max : inst.air_temperature,
              wind: inst.wind_speed };
        while (ts[k + 1] && Date.parse(ts[k + 1].time) < end) k++;
      } else {
        continue;
      }
      var share = snowShare(x.t);
      x.snow = x.p * share * SNOW_CM_PER_MM;
      x.rain = x.p * (1 - share);
      iv.push(x);
    }
    return iv;
  }

  function windowSum(iv, a, b, key) {        // prorates the 6-hour blocks that straddle a window edge
    var s = 0;
    iv.forEach(function (x) {
      var o = Math.min(b, x.e) - Math.max(a, x.s);
      if (o > 0) s += x[key] * o / (x.e - x.s);
    });
    return round1(s);
  }

  function parseMet(list, resorts, nowMs) {
    if (!Array.isArray(list) || list.length !== resorts.length) {
      throw new Error('MET Norway returned ' + (list && list.length) + ' forecasts for ' + resorts.length + ' mountains');
    }
    return resorts.map(function (r, i) {
      var j = list[i];
      if (!j || !j.properties || !j.properties.timeseries || !j.properties.timeseries.length) return { resort: r, missing: true };
      var ts = j.properties.timeseries, iv = metIntervals(ts);
      var w0 = Math.max(iv.length ? iv[0].s : nowMs, Math.floor(nowMs / HOUR) * HOUR);
      iv = iv.filter(function (x) { return x.e > w0; });
      var todayIso = localParts(nowMs, r.tz).date;

      var days = [];
      for (var k = 0; k < 7; k++) {
        days.push({ date: addDays(todayIso, k), snow: 0, rain: 0, tmin: null, tmax: null, wind: null,
                    sky: '', past: false, today: k === 0, _p: -1, _words: {} });
      }
      iv.forEach(function (x) {
        var mid = Math.max(x.s, w0) + (x.e - Math.max(x.s, w0)) / 2;
        var dd = localParts(mid, r.tz).date, day = null;
        for (var n = 0; n < days.length; n++) if (days[n].date === dd) day = days[n];
        if (!day) return;
        var share = Math.min(1, (x.e - Math.max(x.s, w0)) / (x.e - x.s));   // today: only what is still ahead
        day.snow += x.snow * share; day.rain += x.rain * share;
        if (isNum(x.tmin)) day.tmin = day.tmin === null ? x.tmin : Math.min(day.tmin, x.tmin);
        if (isNum(x.tmax)) day.tmax = day.tmax === null ? x.tmax : Math.max(day.tmax, x.tmax);
        if (isNum(x.wind)) day.wind = day.wind === null ? x.wind * 3.6 : Math.max(day.wind, x.wind * 3.6);
        var w = skyMet(x.sym);
        if (x.p >= 0.2 && x.p > day._p) { day._p = x.p; day.sky = w; }
        if (w) day._words[w] = (day._words[w] || 0) + (x.e - x.s);
      });
      days.forEach(function (d) {
        if (!d.sky) {
          var best = 0;
          Object.keys(d._words).forEach(function (w) { if (d._words[w] > best) { best = d._words[w]; d.sky = w; } });
        }
        d.snow = round1(d.snow); d.rain = round1(d.rain);
        delete d._p; delete d._words;
      });

      var hours = [], ticks = [], end72 = w0 + 72 * HOUR;
      iv.forEach(function (x) {
        if (x.s >= end72) return;
        var a = Math.max(x.s, w0), b = Math.min(x.e, end72);
        hours.push({ x0: (a - w0) / (72 * HOUR), x1: (b - w0) / (72 * HOUR), snow: x.snow,
                     rate: x.snow / ((x.e - x.s) / HOUR), frz: null, temp: x.t });
      });
      for (var hh = 1; hh < 72; hh++) {
        var lp = localParts(w0 + hh * HOUR, r.tz);
        if (lp.hour === '00') ticks.push({ x: hh / 72, label: dateParts(lp.date).dow });
      }

      var first = ts[0].data.instant && ts[0].data.instant.details;
      return finish({
        resort: r, src: 'met', hasHistory: false, hasFrz: false, windLabel: 'Wind',
        tz: r.tz, elevation: ts && j.geometry && j.geometry.coordinates ? j.geometry.coordinates[2] : null,
        last24: null, last7: null,
        next24: windowSum(iv, w0, w0 + 24 * HOUR, 'snow'),
        next72: windowSum(iv, w0, w0 + 72 * HOUR, 'snow'),
        next7: windowSum(iv, w0, w0 + 168 * HOUR, 'snow'),
        nowTemp: first ? first.air_temperature : null,
        nowFrz: null, frzMin: null, frzMax: null,
        today: days[0], days: days, hours: hours, ticks: ticks
      });
    });
  }

  /* ---------- formatting ---------- */

  var MINUS = '−';
  function signed(n) { return n < 0 ? MINUS + Math.abs(n) : String(n); }
  function thousands(n) { return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function fmt(units) {
    var imp = units === 'imperial';
    return {
      snowUnit: imp ? 'in' : 'cm',
      snow: function (cm) {
        if (!isNum(cm)) return '–';
        if (!imp) return cm > 0 && cm < 0.5 ? '<1' : String(Math.round(cm));
        var x = cm / 2.54;
        if (x > 0 && x < 0.1) return '<0.1';
        return x < 10 ? x.toFixed(1).replace(/\.0$/, '') : String(Math.round(x));
      },
      temp: function (c) {
        if (!isNum(c)) return '–';
        return signed(Math.round(imp ? c * 9 / 5 + 32 : c)) + '°';
      },
      tempUnit: imp ? '°F' : '°C',
      elev: function (m) { return isNum(m) ? thousands(imp ? m * 3.28084 : m) + (imp ? ' ft' : ' m') : '–'; },
      wind: function (k) { return isNum(k) ? Math.round(imp ? k / 1.609344 : k) + (imp ? ' mph' : ' km/h') : '–'; },
      rain: function (mm) { return imp ? (mm / 25.4).toFixed(2) + ' in' : Math.round(mm) + ' mm'; }
    };
  }

  var DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  var MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  function dateParts(iso) {             // "2026-10-12" -> {dow:'MON', day:12, mon:'OCT'}
    var p = iso.split('-');
    var dt = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    return { dow: DOW[dt.getUTCDay()], day: dt.getUTCDate(), mon: MON[dt.getUTCMonth()], yy: p[0].slice(2) };
  }

  function sky(code) {                  // Open-Meteo WMO weather code
    if (!isNum(code)) return '';
    if (code === 0) return 'Clear';
    if (code <= 3) return 'Cloud';
    if (code === 45 || code === 48) return 'Fog';
    if (code >= 51 && code <= 57) return 'Drizzle';
    if (code >= 61 && code <= 67) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Showers';
    if (code === 85 || code === 86) return 'Snow showers';
    if (code >= 95) return 'Thunder';
    return '';
  }

  function skyMet(sym) {                // MET Norway symbol_code, e.g. "lightsnowshowers_day"
    if (!sym) return '';
    var s = String(sym).replace(/_(day|night|polartwilight)$/, '');
    if (/thunder/.test(s)) return 'Thunder';
    if (/sleet/.test(s)) return 'Sleet';
    if (/snow/.test(s)) return /showers/.test(s) ? 'Snow showers' : 'Snow';
    if (/rain/.test(s)) return /showers/.test(s) ? 'Showers' : 'Rain';
    if (s === 'fog') return 'Fog';
    if (s === 'clearsky' || s === 'fair') return 'Clear';
    if (s === 'partlycloudy' || s === 'cloudy') return 'Cloud';
    return '';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- Avalanche Canada ---------- */

  // Public API, no key, CORS open (api-docs at avalanche.ca/api-docs). The point query returns the product
  // that covers it. Every product carries exactly 3 days of danger ratings: there is no 5-day avalanche
  // forecast. Off-season the point returns one province-wide "Early Season" bulletin with no ratings.
  // Danger colours are Avalanche Canada's own (their site code, checked 12 Oct 2026).
  var AVY_LEVELS = { low: 1, moderate: 2, considerable: 3, high: 4, extreme: 5 };
  var AVY_ELEV = [['alp', 'Alpine'], ['tln', 'Treeline'], ['btl', 'Below treeline']];
  var AVY_LINK = 'https://avalanche.ca/map';
  var BC_TZ = 'Etc/GMT+7';           // BC is UTC-7 all year from 1 Nov 2026; issue times are mid-afternoon, so the date is safe either side

  function avyUrl(lat, lon) {
    return 'https://api.avalanche.ca/forecasts/en/products/point?lat=' + r4(lat) + '&long=' + r4(lon);
  }

  function plain(html) {                // their highlights are HTML; the page shows text only
    return String(html || '').replace(/<\/?(a|b|strong|em|i|span)\b[^>]*>/gi, '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"')
      .replace(/&#39;|&rsquo;/g, '’').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ').trim();
  }

  // Likelihood comes as one step ("likely") or a range of two ("possible_unlikely"), on Avalanche Canada's 5-step scale.
  var AVY_LIKELY = ['unlikely', 'possible', 'likely', 'verylikely', 'certain'];
  var AVY_LIKELY_TEXT = ['Unlikely', 'Possible', 'Likely', 'Very likely', 'Certain'];
  function likelihood(v) {
    var steps = (String(v || '').toLowerCase().match(/very_?likely|unlikely|possible|likely|certain/g) || [])
      .map(function (w) { return AVY_LIKELY.indexOf(w.replace('_', '')); }).filter(function (k) { return k >= 0; });
    if (!steps.length) return { lo: -1, hi: -1, text: '' };
    var lo = Math.min.apply(null, steps), hi = Math.max.apply(null, steps);
    return { lo: lo, hi: hi, text: lo === hi ? AVY_LIKELY_TEXT[lo] : AVY_LIKELY_TEXT[lo] + ' to ' + AVY_LIKELY_TEXT[hi].toLowerCase() };
  }

  function parseAvy(j, nowMs) {
    var rep = j && j.report;
    if (!rep || !Array.isArray(rep.dangerRatings)) throw new Error('no avalanche report');
    var today = localParts(nowMs, BC_TZ).date, season = '';
    var days = rep.dangerRatings.map(function (d) {
      var iso = localParts(Date.parse(d.date && d.date.value), BC_TZ).date, r = {};
      AVY_ELEV.forEach(function (e) {
        var x = (d.ratings && d.ratings[e[0]] && d.ratings[e[0]].rating) || {};
        r[e[0]] = { value: x.value || 'norating', display: x.display || '', level: AVY_LEVELS[x.value] || 0 };
        if (!season && !r[e[0]].level && x.display) season = x.display;
      });
      return { date: iso, weekday: (d.date && d.date.display) || '', ratings: r, past: iso < today, today: iso === today };
    });
    var rated = days.some(function (d) { return AVY_ELEV.some(function (e) { return d.ratings[e[0]].level > 0; }); });
    var title = String(rep.title || ''), valid = Date.parse(rep.validUntil);
    return {
      url: j.url || AVY_LINK,
      owner: (j.owner && j.owner.display) || 'Avalanche Canada',
      area: title.split('-').length <= 12 ? title : '',   // the off-season bulletin names ~95 sub-regions: not worth printing
      issued: Date.parse(rep.dateIssued),
      issuedDate: localParts(Date.parse(rep.dateIssued), BC_TZ).date,
      validUntil: valid,
      expired: isNum(valid) && valid < nowMs,
      rated: rated,
      season: rated ? '' : season,
      days: days,
      highlights: plain(rep.highlights),
      problems: (rep.problems || []).filter(function (x) { return x.type && x.type.display; }).map(function (x) {
        var dd = x.data || {}, sz = dd.expectedSize || {};
        return {
          type: x.type.value || '', name: x.type.display,
          elevations: (dd.elevations || []).map(function (e) { return e.value; }),
          aspects: (dd.aspects || []).map(function (e) { return e.value; }),
          likelihood: likelihood(dd.likelihood && dd.likelihood.value),
          size: { min: parseFloat(sz.min), max: parseFloat(sz.max) }
        };
      }),
      confidence: rep.confidence && rep.confidence.rating && rep.confidence.rating.value !== 'noRating' ? rep.confidence.rating.display : ''
    };
  }

  /* ---------- charts ---------- */

  // Daily strip: with history, 7 past days (light) | today + 6 ahead (dark); forecast-only, today + 6.
  // One scale for every mountain.
  function stripSvg(m, f) {
    var W = 280, n = m.days.length, slot = W / n, bw = Math.min(14, slot * 0.7), top = 14, base = 58;
    var out = ['<svg class="es-strip" viewBox="0 0 ' + W + ' 78" role="img" aria-label="' +
      esc((m.hasHistory ? 'Daily snowfall, last 7 days and next 7 days, ' : 'Forecast snow per day, next 7 days, ') + m.resort.name) + '">'];
    out.push('<line class="es-axis" x1="0" x2="' + W + '" y1="' + base + '" y2="' + base + '"/>');
    var todayX = null;
    m.days.forEach(function (d, k) {
      var cx = k * slot + slot / 2, x = cx - bw / 2;
      var hgt = d.snow > 0 ? Math.max(2, Math.min(1, d.snow / STRIP_CAP_CM) * (base - top)) : 0;
      var cls = d.today ? 'es-bar es-bar--today' : d.past ? 'es-bar es-bar--past' : 'es-bar';
      if (d.today && k > 0) todayX = k * slot;
      if (hgt > 0) {
        var y = base - hgt, r = Math.min(4, bw / 2, hgt);
        // rounded top, square base
        out.push('<path class="' + cls + '" d="M' + x + ' ' + base + 'V' + (y + r) + 'Q' + x + ' ' + y + ' ' + (x + r) + ' ' + y +
          'H' + (x + bw - r) + 'Q' + (x + bw) + ' ' + y + ' ' + (x + bw) + ' ' + (y + r) + 'V' + base + 'Z"/>');
      }
      if (d.snow >= 1) {
        out.push('<text class="es-val" x="' + cx + '" y="' + (base - hgt - 3) + '">' + f.snow(d.snow) + '</text>');
      }
      if (d.rain >= FLAGS.rainMm) {
        out.push('<rect class="es-rain" x="' + x + '" y="' + (base + 2) + '" width="' + bw + '" height="2" rx="1"/>');
      }
      out.push('<text class="es-dow' + (d.today ? ' es-dow--today' : '') + '" x="' + cx + '" y="72">' +
        (d.today ? 'NOW' : n > 7 ? dateParts(d.date).dow.slice(0, 2) : dateParts(d.date).dow) + '</text>');
      out.push('<rect class="es-hit" data-k="' + k + '" x="' + (k * slot) + '" y="0" width="' + slot + '" height="78"/>');
    });
    if (todayX !== null) out.push('<line class="es-today" x1="' + todayX + '" x2="' + todayX + '" y1="6" y2="' + base + '"/>');
    out.push('</svg>');
    return out.join('');
  }

  // 72-hour detail: snow per hour, then either the freezing level against the mountain's top and base
  // (Open-Meteo) or the temperature at the top against 0 degrees (MET Norway). Two charts on one time
  // axis, never two y-scales on one chart.
  function detailSvg(m, f) {
    var hrs = m.hours;
    if (!hrs.length) return '';
    var W = 288, X = function (v) { return v * W; };
    var cap = Math.ceil(Math.max(1, Math.max.apply(null, hrs.map(function (x) { return x.rate; }))));
    var out = ['<svg class="es-detail" viewBox="0 0 ' + W + ' 176" role="img" aria-label="' +
      esc('Next 72 hours: snow per hour and ' + (m.hasFrz ? 'freezing level' : 'temperature at the top') + ', ' + m.resort.name) + '">'];
    // panel 1: snow per hour (y 16..64)
    out.push('<text class="es-cap" x="0" y="10">SNOW PER HOUR · MAX ' + f.snow(cap) + ' ' + f.snowUnit + '</text>');
    out.push('<line class="es-axis" x1="0" x2="' + W + '" y1="64" y2="64"/>');
    hrs.forEach(function (x) {
      if (x.rate <= 0) return;
      var hgt = Math.max(1.5, (x.rate / cap) * 48), x0 = X(x.x0) + 0.4, w = Math.max(1, X(x.x1) - X(x.x0) - 0.8);
      out.push('<rect class="es-bar" x="' + x0.toFixed(2) + '" y="' + (64 - hgt).toFixed(2) + '" width="' + w.toFixed(2) +
        '" height="' + hgt.toFixed(2) + '" rx="0.8"/>');
    });
    // panel 2 (y 92..156)
    var key = m.hasFrz ? 'frz' : 'temp';
    var vals = hrs.map(function (x) { return x[key]; }).filter(isNum);
    var refs = m.hasFrz
      ? [['TOP ' + f.elev(m.resort.top), m.resort.top], [(m.resort.baseLabel || 'BASE') + ' ' + f.elev(m.resort.base), m.resort.base]]
      : [[f.temp(0), 0]];
    refs = refs.filter(function (r) { return isNum(r[1]); });
    var all = vals.concat(refs.map(function (r) { return r[1]; }));
    var lo = Math.min.apply(null, all), hiV = Math.max.apply(null, all);
    var pad = m.hasFrz ? Math.max(150, (hiV - lo) * 0.12) : Math.max(1.5, (hiV - lo) * 0.12);
    lo = m.hasFrz ? Math.max(0, lo - pad) : lo - pad; hiV = hiV + pad;
    var y = function (v) { return 156 - ((v - lo) / (hiV - lo)) * 64; };
    out.push('<text class="es-cap" x="0" y="86">' + (m.hasFrz ? 'FREEZING LEVEL' : 'TEMPERATURE AT THE TOP') + '</text>');
    refs.forEach(function (ref) {
      var yy = y(ref[1]);
      out.push('<line class="es-ref" x1="0" x2="' + W + '" y1="' + yy.toFixed(1) + '" y2="' + yy.toFixed(1) + '"/>');
      out.push('<text class="es-reflabel" x="' + W + '" y="' + (yy - 3).toFixed(1) + '">' + ref[0] + '</text>');
    });
    var pts = [];
    hrs.forEach(function (x) {
      if (isNum(x[key])) pts.push(X((x.x0 + x.x1) / 2).toFixed(1) + ',' + y(x[key]).toFixed(1));
    });
    if (pts.length) out.push('<polyline class="es-line" points="' + pts.join(' ') + '"/>');
    // shared time axis: a tick at each local midnight
    m.ticks.forEach(function (t) {
      var xx = X(t.x).toFixed(1);
      out.push('<line class="es-tick" x1="' + xx + '" x2="' + xx + '" y1="16" y2="160"/>');
      out.push('<text class="es-tlabel" x="' + (X(t.x) + 3).toFixed(1) + '" y="172">' + t.label + '</text>');
    });
    out.push('</svg>');
    return out.join('');
  }

  /* ---------- page ---------- */

  function chip(label, cls) { return '<span class="es-chip ' + cls + '">' + label + '</span>'; }

  function opensChip(r, todayIso) {
    if (!r.opens || !todayIso || r.opens <= todayIso) return '';
    var p = dateParts(r.opens);
    return chip('TARGET OPEN ' + p.day + ' ' + p.mon, 'es-chip--quiet');
  }

  function num3(label, v, f, main) {
    return '<div' + (main ? ' class="es-main"' : '') + '><dt>' + label + '</dt><dd>' + f.snow(v) + '<i>' + f.snowUnit + '</i></dd></div>';
  }

  function card(m, f, rank, todayIso, open) {
    if (m.missing) return bareCard(m.resort, 'No forecast for this mountain right now.');
    var r = m.resort, t = m.today || {};
    var chips = [];
    if (m.flags.storm) chips.push(chip('STORM ' + f.snow(m.next72) + ' ' + f.snowUnit + ' / 72H', 'es-chip--alert'));
    if (m.flags.fresh) chips.push(chip('FRESH ' + f.snow(m.last24) + ' ' + f.snowUnit, 'es-chip--alert'));
    if (m.flags.rain) chips.push(chip('RAIN AT TOP ' + dateParts(m.rainDay.date).dow, 'es-chip--warn'));
    var oc = opensChip(r, todayIso);
    if (oc) chips.push(oc);
    var where = [r.region, r.country].filter(Boolean).join(' · ');
    var nums = m.hasHistory
      ? num3('Last 24 h', m.last24, f) + num3('Last 7 days', m.last7, f) + num3('Next 7 days', m.next7, f, true)
      : num3('Next 24 h', m.next24, f) + num3('Next 72 h', m.next72, f) + num3('Next 7 days', m.next7, f, true);
    return '<li class="es-card" data-id="' + esc(r.id) + '">' +
      '<div class="es-card__head">' +
        (rank ? '<span class="es-rank">' + (rank < 10 ? '0' + rank : rank) + '</span>' : '') +
        '<div class="es-card__name"><h3 class="es-name">' + esc(r.name) + '</h3>' +
        '<p class="es-where">' + esc(where) + ' · TOP ' + (r.approx ? '≈' : '') + f.elev(r.top) + '</p></div>' +
      '</div>' +
      (chips.length ? '<div class="es-chips">' + chips.join('') + '</div>' : '') +
      '<dl class="es-nums">' + nums + '</dl>' +
      '<div class="es-stripwrap">' + stripSvg(m, f) + '</div>' +
      '<p class="es-meta">' +
        '<span>NOW ' + f.temp(m.nowTemp) + '</span>' +
        '<span>TODAY ' + f.temp(t.tmin) + ' / ' + f.temp(t.tmax) + (t.sky ? ' ' + t.sky.toUpperCase() : '') + '</span>' +
        (m.hasFrz ? '<span>FREEZING ' + f.elev(m.nowFrz) + '</span>' : '') +
        '<span>' + m.windLabel.toUpperCase() + ' ' + f.wind(t.wind) + '</span>' +
      '</p>' +
      camsRow(r) +
      '<div class="es-actions">' +
        linkSet(r) +
        '<button type="button" class="es-more" aria-expanded="' + (open ? 'true' : 'false') + '">' + (open ? 'Hide 72 h' : 'Next 72 h') + '</button>' +
      '</div>' +
      '<div class="es-detailwrap"' + (open ? '' : ' hidden') + '>' + (open ? detailBlock(m, f) : '') + '</div>' +
      '</li>';
  }

  function detailBlock(m, f) {
    var lines = ['Next 24 h ' + f.snow(m.next24) + ' ' + f.snowUnit, 'Next 72 h ' + f.snow(m.next72) + ' ' + f.snowUnit];
    if (isNum(m.frzMin) && isNum(m.frzMax)) lines.push('Freezing level ' + f.elev(m.frzMin) + ' to ' + f.elev(m.frzMax));
    if (m.rainDay) lines.push('Rain at the top ' + dateParts(m.rainDay.date).dow + ', ' + f.rain(m.rainDay.rain));
    return '<p class="es-detailnums">' + lines.map(esc).join(' · ') + '</p>' + detailSvg(m, f);
  }

  function reportLink(r) {
    if (!r.report) return '';
    return '<a class="es-link" href="' + esc(r.report) + '" target="_blank" rel="noopener">' +
      esc(r.reportLabel || 'Official snow report') + ' ↗</a>';
  }

  function extLink(url, label) {
    return '<a class="es-link" href="' + esc(url) + '" target="_blank" rel="noopener">' + esc(label) + ' ↗</a>';
  }

  // The resort's own report, any extra links (Whistler: Peak Live) and, when the page has no per-cam links,
  // the resort's own cams page.
  function linkSet(r) {
    var out = [reportLink(r)];
    (r.links || []).forEach(function (l) { if (l && l.url) out.push(extLink(l.url, l.label || 'More')); });
    if (r.camsPage && !(r.cams && r.cams.length)) out.push(extLink(r.camsPage, 'Cams'));
    out = out.filter(Boolean);
    return out.length ? '<span class="es-linkset">' + out.join('') + '</span>' : '';
  }

  function camsRow(r) {                 // one link per live cam (personal page)
    if (!r.cams || !r.cams.length) return '';
    return '<p class="es-cams"><span class="es-camlabel">Cams</span>' +
      r.cams.map(function (c) { return extLink(c.url, c.label); }).join('') + '</p>';
  }

  function bareCard(r, note) {
    var where = [r.region, r.country].filter(Boolean).join(' · ');
    var links = linkSet(r);
    return '<li class="es-card es-card--bare"><div class="es-card__head"><div class="es-card__name">' +
      '<h3 class="es-name">' + esc(r.name) + '</h3><p class="es-where">' + esc(where) + '</p></div></div>' +
      (note ? '<p class="es-meta">' + esc(note) + '</p>' : '') + camsRow(r) +
      (links ? '<div class="es-actions">' + links + '</div>' : '') +
      '</li>';
  }

  // ---- backcountry block: our own icons, drawn from Avalanche Canada's data (no graphics or long text copied) ----

  var AVY_ASPECTS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
  var AVY_LEVEL_TEXT = ['', 'Low', 'Moderate', 'Considerable', 'High', 'Extreme'];

  // A mountain split into its three bands, alpine on top. fills = {alp, tln, btl} -> CSS class per band.
  function mountainSvg(fills, delay) {
    var bands = [['alp', 2, 11], ['tln', 12, 19.5], ['btl', 20.5, 28]];
    function hw(y) { return 16 * (y - 2) / 26; }
    return '<svg class="es-mtn" viewBox="0 0 36 30" aria-hidden="true">' + bands.map(function (b, k) {
      var y1 = b[1], y2 = b[2], a = hw(y1), c = hw(y2);
      var pts = [[18 - a, y1], [18 + a, y1], [18 + c, y2], [18 - c, y2]].map(function (q) { return q[0].toFixed(2) + ',' + q[1]; }).join(' ');
      return '<polygon class="es-band ' + (fills[b[0]] || 'es-band--off') + '" style="--d:' + (delay + (2 - k) * 90) + '" points="' + pts + '"/>';
    }).join('') + '</svg>';
  }

  // Eight slope aspects as a rose, north up; affected aspects filled. Wedges appear clockwise from north.
  function roseSvg(aspects) {
    var cx = 18, cy = 21, R = 14.5, r = 4.5, out = ['<svg class="es-rose" viewBox="0 0 36 37" aria-hidden="true"><text x="18" y="5" class="es-rose__n">N</text>'];
    AVY_ASPECTS.forEach(function (a, i) {
      var t0 = (i * 45 - 22.5 - 90) * Math.PI / 180, t1 = (i * 45 + 22.5 - 90) * Math.PI / 180;
      var P = function (rad, t) { return (cx + rad * Math.cos(t)).toFixed(2) + ' ' + (cy + rad * Math.sin(t)).toFixed(2); };
      var on = aspects.indexOf(a) >= 0;
      out.push('<path class="es-wedge' + (on ? ' es-wedge--on' : '') + '" style="--d:' + (i * 70) + '" d="M' + P(R, t0) + ' A' + R + ' ' + R + ' 0 0 1 ' + P(R, t1) +
        ' L' + P(r, t1) + ' A' + r + ' ' + r + ' 0 0 0 ' + P(r, t0) + 'Z"/>');
    });
    out.push('</svg>');
    return out.join('');
  }

  // Small line glyph per problem family (slab, loose, cornice, glide) with a modifier for storm / wind / persistent / wet.
  function problemGlyph(type) {
    var t = String(type || '').toLowerCase(), g = ['<svg class="es-glyph" viewBox="0 0 32 32" aria-hidden="true">'];
    var slope = '<path class="es-g-line" d="M3 28 L29 10"/>';
    var slab = '<polygon class="es-g-fill" points="9.5,23.5 22.5,14.5 19.7,10.4 6.7,19.4"/>';
    var drop = '<path class="es-g-fill" d="M26 18 C28 21 29 22.5 29 24 A3 3 0 0 1 23 24 C23 22.5 24 21 26 18Z"/>';
    if (/cornice/.test(t)) {
      g.push('<path class="es-g-line" d="M3 28 L17 13"/><path class="es-g-fill" d="M17 13 C21 9 26 8.5 30 9.5 C27 11 23 12.5 20.5 15.5 Z"/>');
    } else if (/glide/.test(t)) {
      g.push(slope, slab, '<path class="es-g-line" d="M16 19 L18.5 22.5"/>');
    } else if (/loose/.test(t)) {
      g.push(slope, '<path class="es-g-fill" d="M24 9 C20 13 15 17.5 9 22.5 L13.5 24.5 C17.5 19 21 14 24 9Z"/><circle class="es-g-fill" cx="24" cy="9" r="1.8"/>');
      if (/wet/.test(t)) g.push(drop);
    } else {
      g.push(slope, slab);
      if (/deep/.test(t)) g.push('<path class="es-g-dash" d="M8 27 L26 14.5"/>');
      else if (/persistent/.test(t)) g.push('<path class="es-g-dash" d="M10.5 25 L24 15.7"/>');
      if (/storm/.test(t)) g.push('<circle class="es-g-fill" cx="5" cy="9" r="1.4"/><circle class="es-g-fill" cx="9.5" cy="5" r="1.4"/><circle class="es-g-fill" cx="13" cy="9.5" r="1.4"/>');
      if (/wind/.test(t)) g.push('<path class="es-g-line" d="M2.5 5.5 H11 C13 5.5 13 3 11.5 3"/><path class="es-g-line" d="M4.5 10 H13.5"/>');
      if (/wet/.test(t)) g.push(drop);
    }
    g.push('</svg>');
    return g.join('');
  }

  // Five-segment meter. level: the danger rating (segments filled in its colour); or a range lo..hi (likelihood).
  function meter(cls, filled, delay, pulse) {
    var out = '<span class="es-meter ' + cls + '" aria-hidden="true">';
    for (var k = 0; k < 5; k++) {
      var on = filled(k);
      out += '<i class="es-mseg' + (on ? ' es-mseg--on' : '') + (on && pulse && !filled(k + 1) ? ' es-mseg--pulse' : '') +
        '" style="--d:' + (delay + k * 70) + '"></i>';
    }
    return out + '</span>';
  }

  // Expected size on Avalanche Canada's 1-5 scale, as a continuous range (sizes come in halves, e.g. 1-2.5).
  function sizeBar(min, max, delay) {
    if (!isNum(min) || !isNum(max)) return '';
    var x = function (v) { return Math.max(0, Math.min(100, (v - 1) / 4 * 100)); };
    var l = x(min), w = Math.max(3, x(max) - l);
    var ticks = '';
    for (var k = 0; k < 5; k++) ticks += '<i class="es-size__tick" style="left:' + (k * 25) + '%"></i>';
    return '<span class="es-size" aria-hidden="true">' + ticks + '<i class="es-size__fill" style="left:' + l.toFixed(1) + '%;width:' + w.toFixed(1) + '%;--d:' + delay + '"></i></span>';
  }

  function num1(v) { return Math.round(v * 10) / 10 === Math.round(v) ? String(Math.round(v)) : (Math.round(v * 10) / 10).toFixed(1); }

  function aspectText(as) {
    if (as.length >= 8) return 'All aspects';
    return AVY_ASPECTS.filter(function (a) { return as.indexOf(a) >= 0; }).map(function (a) { return a.toUpperCase(); }).join(' · ');
  }

  function elevText(es) {
    if (es.length >= 3) return 'All elevations';
    return AVY_ELEV.filter(function (e) { return es.indexOf(e[0]) >= 0; }).map(function (e) { return e[1]; }).join(' · ');
  }

  function dayHtml(d, i) {
    var p = dateParts(d.date), fills = {};
    AVY_ELEV.forEach(function (e) { var x = d.ratings[e[0]]; fills[e[0]] = x.level ? 'es-avy--' + x.level : 'es-band--off'; });
    var rows = AVY_ELEV.map(function (e, r) {
      var x = d.ratings[e[0]], lv = x.level;
      return '<div class="es-avy__row"><dt>' + e[1] + '</dt><dd>' +
        meter('es-meter--' + (lv || 0), function (k) { return k < lv; }, i * 260 + r * 90, lv >= 4) +
        '<span class="es-avy__lv es-avy__lv--' + (lv || 0) + '">' + (lv ? lv + ' · ' + AVY_LEVEL_TEXT[lv] : esc(x.display || 'No rating')) + '</span></dd></div>';
    }).join('');
    return '<div class="es-avy__day"><div class="es-avy__dayhead">' + mountainSvg(fills, i * 260) +
      '<p class="es-avy__date">' + p.dow + ' ' + p.day + ' ' + p.mon + (d.today ? ' · <b>TODAY</b>' : '') + '</p></div>' +
      '<dl>' + rows + '</dl></div>';
  }

  function problemHtml(x, i) {
    var elev = {};
    AVY_ELEV.forEach(function (e) { elev[e[0]] = x.elevations.indexOf(e[0]) >= 0 ? 'es-band--hit' : 'es-band--off'; });
    var lk = x.likelihood, base = 600 + i * 200;
    return '<li class="es-prob"><div class="es-prob__head">' + problemGlyph(x.type) + '<h4 class="es-prob__name">' + esc(x.name) + '</h4></div>' +
      '<dl class="es-prob__facts">' +
        '<div><dt>Elevation</dt><dd>' + mountainSvg(elev, base) + '<span>' + esc(elevText(x.elevations)) + '</span></dd></div>' +
        '<div><dt>Slopes</dt><dd>' + roseSvg(x.aspects) + '<span>' + esc(aspectText(x.aspects)) + '</span></dd></div>' +
        '<div><dt>Likelihood</dt><dd>' + (lk.lo >= 0 ? meter('es-meter--ink', function (k) { return k >= lk.lo && k <= lk.hi; }, base, false) : '') +
          '<span>' + esc(lk.text || '–') + '</span></dd></div>' +
        '<div><dt>Size</dt><dd>' + sizeBar(x.size.min, x.size.max, base + 150) +
          '<span>' + (isNum(x.size.min) ? (x.size.min === x.size.max ? num1(x.size.min) : num1(x.size.min) + '–' + num1(x.size.max)) + ' of 5' : '–') + '</span></dd></div>' +
      '</dl></li>';
  }

  // Backcountry block, leading the Canada group. mode 'ratings': Avalanche Canada's forecast for the point,
  // drawn with our own meters and icons (falls back to the link while loading or if the fetch fails).
  // mode 'link': a pointer to Avalanche Canada only.
  function avyBlock(a, opts, status) {
    if (!opts || opts.mode === 'off' || !opts.mode) return '';
    var head = '<p class="es-avy__label">Backcountry · Avalanche forecast</p>' +
      '<h3 class="es-name">' + esc(opts.title || 'Whistler area') + '</h3>';
    var body = '', link = AVY_LINK, linkLabel = 'Avalanche Canada forecast', key = 'link';
    if (opts.mode === 'ratings' && a) {
      link = a.url; linkLabel = 'Full forecast and travel advice at ' + a.owner;
      key = a.issued + '|' + (a.days[0] && a.days[0].date) + '|' + a.expired;
      if (a.area) head += '<p class="es-where">' + esc(a.area.split('-').join(' · ')) + '</p>';
      var ahead = a.days.filter(function (d) { return !d.past; });
      if (!a.rated) {
        body = '<div class="es-chips">' + chip(esc((a.season || 'No rating').toUpperCase()), 'es-avy--season') + '</div>' +
          (a.highlights ? '<p class="es-avy__text">' + esc(a.highlights) + '</p>' : '');
      } else if (a.expired || !ahead.length) {
        body = '<div class="es-chips">' + chip('EXPIRED', 'es-chip--warn') + '</div>' +
          '<p class="es-avy__text">' + esc(a.owner) + ' hasn’t published a newer forecast here yet. Check their site before you go.</p>';
      } else {
        body = (a.highlights ? '<p class="es-avy__text">' + esc(a.highlights) + '</p>' : '') +
          '<div class="es-avy__days">' + ahead.map(dayHtml).join('') + '</div>' +
          (a.problems.length
            ? '<p class="es-avy__sub">Avalanche problems</p><ol class="es-probs">' + a.problems.map(problemHtml).join('') + '</ol>'
            : '<p class="es-avy__sub">No avalanche problems listed</p>');
      }
      var ip = dateParts(a.issuedDate);
      body += '<p class="es-meta"><span>Issued ' + ip.dow + ' ' + ip.day + ' ' + ip.mon + ' by ' + esc(a.owner) + '</span>' +
        (a.rated && !a.expired ? '<span>3-day forecast</span>' : '') +
        (a.rated && !a.expired && a.confidence ? '<span>Confidence: ' + esc(a.confidence) + '</span>' : '') + '</p>';
    } else {
      body = '<p class="es-avy__text">Heading out of bounds? Read Avalanche Canada’s forecast before you go. It rates the danger ' +
        'in the alpine, at treeline and below treeline for the next three days.</p>';
    }
    return '<div class="es-avy" data-key="' + esc(key + '|' + (status || '')) + '">' + '<div class="es-avy__head">' + head + '</div>' + body +
      (status ? '<p class="es-status">' + esc(status) + '</p>' : '') +
      '<div class="es-actions">' + extLink(link, linkLabel) + '</div></div>';
  }

  function shellHtml(cfg) {
    var titleTag = cfg.titleTag || 'h1';
    return '<div class="es-head">' +
        '<p class="es-disp" data-es="disp">' + (cfg.dispatch || 'SNOW REPORT') + '</p>' +   // trusted: built by the page, may hold <b>
        '<' + titleTag + ' class="es-title">' + esc(cfg.title || 'Where it’s snowing') + '</' + titleTag + '>' +
        (cfg.lede ? '<p class="es-lede">' + esc(cfg.lede) + '</p>' : '') +
        '<div class="es-top" data-es="top"></div>' +
        '<div class="es-controls" role="group" aria-label="Sort and units"><span class="es-sortlabel">Sort</span>' +
          '<div class="es-seg" data-es="sort">' +
            '<button type="button" data-v="region">Region</button>' +
            '<button type="button" data-v="next7">Snow ahead</button>' +
            '<button type="button" data-v="last7">Last 7 days</button>' +
          '</div>' +
          '<div class="es-seg" data-es="units">' +
            '<button type="button" data-v="metric">cm · °C</button>' +
            '<button type="button" data-v="imperial">in · °F</button>' +
          '</div>' +
        '</div>' +
        '<p class="es-key"><span data-es="keypast"><i class="es-sw es-sw--past"></i>Snow, last 7 days</span>' +
          '<span><i class="es-sw"></i>Snow forecast</span><span><i class="es-sw es-sw--rain"></i>Rain at the top</span>' +
          '<span>Hover or tap a day</span></p>' +
        '<p class="es-status" data-es="status" role="status"></p>' +
      '</div>' +
      '<div class="es-body" data-es="body"></div>' +
      '<p class="es-foot">' + (cfg.foot || '') + '</p>' +
      '<div class="es-tip" data-es="tip" hidden></div>';
  }

  function mount(root, cfg) {
    var store = cfg.storage || { get: function () { return null; }, set: function () {} };
    var parser = cfg.parse || parse;
    var state = {
      models: null, error: null,
      sort: store.get('es-sort') || cfg.sort || 'region',
      units: store.get('es-units') || cfg.units || 'metric',
      open: {}, avy: null, avyStatus: '',
      groups: readGroups()           // which region dropdowns are open (all closed until the visitor opens one)
    };
    function readGroups() { try { return JSON.parse(store.get('es-groups') || '{}') || {}; } catch (e) { return {}; } }
    var avyGroup = (cfg.avy && cfg.avy.group) || 'CA';
    function avyHtml() { return avyBlock(state.avy, cfg.avy, state.avyStatus); }
    function groupHtml(g, n, lead, inner) {
      return '<details class="es-group es-drop" data-g="' + esc(g) + '"' + (state.groups[g] ? ' open' : '') + '>' +
        '<summary class="es-sum"><span class="es-sumname"><span class="es-gtitle">' + esc(GROUP_NAMES[g] || g) + '</span>' +
        '<span class="es-gcount">' + n + (n === 1 ? ' mountain' : ' mountains') + '</span></span>' +
        '<span class="es-gbest">' + lead + '</span><span class="es-chev" aria-hidden="true"></span></summary>' +
        '<div class="es-drop__body">' + inner + '</div></details>';
    }
    root.innerHTML = shellHtml(cfg);
    var $ = function (k) { return root.querySelector('[data-es="' + k + '"]'); };

    function live() { return (state.models || []).filter(function (m) { return !m.missing; }); }
    function history() { var l = live(); return !!(l.length && l[0].hasHistory); }

    function setSeg(name, v) {
      Array.prototype.forEach.call($(name).querySelectorAll('button'), function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-v') === v ? 'true' : 'false');
        if (name === 'sort' && b.getAttribute('data-v') === 'last7') b.hidden = !!state.models && !history();
      });
    }

    function todayIso() {
      var l = live();
      return l.length && l[0].today ? l[0].today.date : null;
    }

    function renderTop(f) {
      var ms = live().sort(function (a, b) { return b.next7 - a.next7; });
      var best = ms.filter(function (m) { return m.next7 >= 1; }).slice(0, 3);
      var html;
      if (!best.length) {
        html = '<p class="es-quiet">Quiet week. None of these ' + ms.length + ' mountains is forecast more than a trace of snow in the next 7 days.</p>';
      } else {
        html = '<p class="es-toplabel">Most snow, next 7 days</p><ol class="es-toplist">' + best.map(function (m) {
          return '<li><span class="es-topname">' + esc(m.resort.name) + '</span><span class="es-topval">' + f.snow(m.next7) + ' ' + f.snowUnit + '</span></li>';
        }).join('') + '</ol>';
      }
      $('top').innerHTML = html;
    }

    function render() {
      if (state.models && state.sort === 'last7' && !history()) state.sort = 'region';
      setSeg('sort', state.sort);
      setSeg('units', state.units);
      $('keypast').hidden = !!state.models && !history();
      var body = $('body');
      if (!state.models) {             // no forecast yet (loading or failed): the mountains and their own reports
        body.innerHTML = (cfg.groups || GROUP_ORDER).map(function (g) {
          var rs = cfg.resorts.filter(function (r) { return (r.group || 'CA') === g; });
          return rs.length ? groupHtml(g, rs.length, '', (g === avyGroup ? avyHtml() : '') +
            '<ol class="es-list">' + rs.map(function (r) { return bareCard(r); }).join('') + '</ol>') : '';
        }).join('');
        return;
      }
      var f = fmt(state.units), ti = todayIso();
      renderTop(f);
      var html = '';
      if (state.sort === 'region') {
        // One dropdown per region (Max, 14 Oct 2026: "make it drop downs now - it's getting too long"). The summary row
        // names the region, counts its mountains and gives its snowiest one, so the page reads closed.
        (cfg.groups || GROUP_ORDER).forEach(function (g) {
          var ms = state.models.filter(function (m) { return (m.resort.group || 'CA') === g; });
          if (!ms.length) return;
          var best = ms.filter(function (m) { return !m.missing; }).sort(function (a, b) { return b.next7 - a.next7; })[0];
          var lead = best && best.next7 >= 1
            ? 'Most snow: ' + esc(best.resort.name) + ' <b>' + f.snow(best.next7) + ' ' + f.snowUnit + '</b>'
            : 'No snow in the next 7 days';
          html += groupHtml(g, ms.length, lead, (g === avyGroup ? avyHtml() : '') + '<ol class="es-list">' +
            ms.map(function (m) { return card(m, f, 0, ti, state.open[m.resort.id]); }).join('') + '</ol>');
        });
      } else {
        var key = state.sort;
        var val = function (m) { return m.missing ? -1 : m[key]; };
        var ms = state.models.slice().sort(function (a, b) { return val(b) - val(a) || a.resort.order - b.resort.order; });
        var cards = ms.map(function (m, k) { return card(m, f, m.missing ? 0 : k + 1, ti, state.open[m.resort.id]); });
        html = '<section class="es-group">' + avyHtml() + '<ol class="es-list">' + cards.slice(0, RANK_SHOWN).join('') + '</ol>' +
          (cards.length > RANK_SHOWN
            ? '<details class="es-rest" data-g="rest"' + (state.groups.rest ? ' open' : '') + '><summary class="es-restsum">' +
              'The other ' + (cards.length - RANK_SHOWN) + ' mountains</summary><ol class="es-list">' + cards.slice(RANK_SHOWN).join('') + '</ol></details>'
            : '') + '</section>';
      }
      var keep = body.querySelector('.es-avy');
      body.innerHTML = html;
      var fresh = body.querySelector('.es-avy');      // same forecast as before: keep the old node so its motion isn't replayed
      if (keep && fresh && keep.getAttribute('data-key') === fresh.getAttribute('data-key')) fresh.parentNode.replaceChild(keep, fresh);
    }

    function byId(id) {
      for (var i = 0; i < state.models.length; i++) if (state.models[i].resort.id === id) return state.models[i];
      return null;
    }

    // controls
    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('button') : null;
      if (!b || !root.contains(b)) return;
      var seg = b.parentNode && b.parentNode.getAttribute('data-es');
      if (seg === 'sort' || seg === 'units') {
        state[seg] = b.getAttribute('data-v');
        store.set('es-' + seg, state[seg]);
        render();
        return;
      }
      if (b.classList.contains('es-more')) {
        var li = b.closest('.es-card'), id = li.getAttribute('data-id'), m = byId(id);
        state.open[id] = !state.open[id];
        var wrap = li.querySelector('.es-detailwrap');
        wrap.hidden = !state.open[id];
        wrap.innerHTML = state.open[id] && m ? detailBlock(m, fmt(state.units)) : '';
        b.setAttribute('aria-expanded', state.open[id] ? 'true' : 'false');
        b.textContent = state.open[id] ? 'Hide 72 h' : 'Next 72 h';
      }
    });

    // region dropdowns: remember each one per visitor, and replay the avalanche meters when theirs opens
    root.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || !d.classList || !d.hasAttribute('data-g')) return;
      var g = d.getAttribute('data-g');
      if (!!state.groups[g] === d.open) return;
      state.groups[g] = d.open;
      store.set('es-groups', JSON.stringify(state.groups));
      var avy = d.open && d.querySelector('.es-avy');
      if (avy) avy.parentNode.replaceChild(avy.cloneNode(true), avy);
    }, true);

    // tooltip on the daily strip (hover, or tap on a phone)
    var tip = $('tip');
    function showTip(e) {
      var hit = e.target.closest ? e.target.closest('.es-hit') : null;
      if (!hit || !state.models) { tip.hidden = true; return; }
      var li = hit.closest('.es-card'), m = byId(li.getAttribute('data-id'));
      var d = m && m.days && m.days[+hit.getAttribute('data-k')];
      if (!d) { tip.hidden = true; return; }
      var f = fmt(state.units), p = dateParts(d.date);
      var rows = [
        '<b>' + p.dow + ' ' + p.day + ' ' + p.mon + (d.today ? ' · TODAY' : d.past ? '' : ' · FORECAST') + '</b>',
        'Snow ' + f.snow(d.snow) + ' ' + f.snowUnit + (m.hasHistory ? '' : ' (estimate)'),
        (d.rain >= 0.5 ? 'Rain ' + f.rain(d.rain) : null),
        f.temp(d.tmin) + ' / ' + f.temp(d.tmax) + (d.sky ? ' · ' + d.sky : ''),
        m.windLabel + ' ' + f.wind(d.wind)
      ].filter(Boolean);
      tip.innerHTML = rows.join('<br>');
      tip.hidden = false;
      var rr = root.getBoundingClientRect(), hr = hit.getBoundingClientRect();
      var x = hr.left - rr.left + hr.width / 2, yy = hr.top - rr.top;
      var tw = tip.offsetWidth;
      x = Math.max(4 + tw / 2, Math.min(rr.width - 4 - tw / 2, x));
      tip.style.left = x + 'px';
      tip.style.top = (yy - tip.offsetHeight - 6) + 'px';
    }
    root.addEventListener('pointerover', showTip);
    root.addEventListener('pointerdown', showTip);
    root.addEventListener('pointerleave', function () { tip.hidden = true; });

    render();

    var ctrl = {
      update: function (json, nowMs, statusText) {
        try {
          state.models = parser(json, cfg.resorts, nowMs || Date.now());
          state.error = null;
          root.classList.remove('es-has-error');
          if (cfg.dispatchFor) $('disp').innerHTML = cfg.dispatchFor(state.models);
          $('status').textContent = statusText || '';
          render();
          return state.models;
        } catch (err) {
          ctrl.fail('The forecast came back in a shape this page can’t read (' + err.message + ').');
          return null;
        }
      },
      fail: function (msg) {
        state.error = msg;
        $('status').textContent = msg;
        root.classList.add('es-has-error');
      },
      status: function (msg) { $('status').textContent = msg || ''; },
      models: function () { return state.models; },
      // Avalanche Canada product for the backcountry block (any JSON the point query returns)
      avy: function (json, nowMs, statusText) {
        try { state.avy = parseAvy(json, nowMs || Date.now()); state.avyStatus = statusText || ''; }
        catch (err) { state.avyStatus = 'The avalanche forecast came back in a shape this page can’t read.'; }
        render();
        return state.avy;
      },
      avyStatus: function (msg) { state.avyStatus = msg || ''; render(); }
    };
    return ctrl;
  }

  var api = { buildUrl: buildUrl, parse: parse, metUrl: metUrl, trimMet: trimMet, parseMet: parseMet, snowShare: snowShare,
              avyUrl: avyUrl, parseAvy: parseAvy, AVY_LINK: AVY_LINK, mount: mount, fmt: fmt, dateParts: dateParts, FLAGS: FLAGS, DAILY: DAILY, HOURLY: HOURLY,
              stripSvg: stripSvg, esc: esc };   // stripSvg + esc: the home-page strip (edi-snow-strip) draws its own compact cards
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else global.EdiSnow = api;
})(typeof window !== 'undefined' ? window : this);

/* EDI snow strip: the home page's short snow report (Max, 14 Oct 2026: "Add the snow report to the endeavordesigninc.com
   home page under the first banner"). Runs after src/snow-report.js (build.py concatenates the two into
   assets/edi-snow-strip.js, loaded with defer).
   - Same 28 mountains, same MET Norway forecast and the same sums as /pages/snow-report: parseMet() from the engine.
   - Same session cache key as the page ('es-met:' + every MET URL), so a visitor who goes on to the full report
     doesn't fetch again, and one who comes from it doesn't fetch at all.
   - Shows the mountains with the most snow in the next 7 days (count set in the section), each card linking to the
     full report. It waits until the strip is near the screen before it calls MET. */
(function () {
  'use strict';
  var MONS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  var MAX_AGE = 30 * 60 * 1000;

  function stamp(ms) {
    try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }); }
    catch (e) { return new Date(ms).toLocaleTimeString(); }
  }

  function boot(wrap) {
    var E = window.EdiSnow;
    if (!E || !wrap || wrap.getAttribute('data-es-ready')) return;
    wrap.setAttribute('data-es-ready', '1');
    var cfg = JSON.parse(wrap.querySelector('script[data-es-config]').textContent);
    cfg.resorts.forEach(function (r, i) {
      r.lat = parseFloat(r.lat); r.lon = parseFloat(r.lon); r.top = parseFloat(r.top); r.base = parseFloat(r.base); r.order = i;
    });
    var f = E.fmt(cfg.units), esc = E.esc, count = Math.max(1, parseInt(cfg.count, 10) || 6);
    var $ = function (k) { return wrap.querySelector('[data-ess="' + k + '"]'); };

    var now = new Date();
    $('date').textContent = ('0' + now.getDate()).slice(-2) + ' ' + MONS[now.getMonth()] + ' ' + String(now.getFullYear()).slice(2);

    function chip(label, cls) { return '<span class="es-chip ' + cls + '">' + esc(label) + '</span>'; }

    function card(m, k) {
      var r = m.resort, chips = [];
      if (m.flags.storm) chips.push(chip('STORM ' + f.snow(m.next72) + ' ' + f.snowUnit + ' / 72H', 'es-chip--alert'));
      if (m.flags.rain) chips.push(chip('RAIN AT TOP ' + E.dateParts(m.rainDay.date).dow, 'es-chip--warn'));
      var today = m.today && m.today.date;
      if (r.opens && today && r.opens > today) {
        var p = E.dateParts(r.opens);
        chips.push(chip('TARGET OPEN ' + p.day + ' ' + p.mon, 'es-chip--quiet'));
      }
      var rank = k + 1;
      return '<li class="ess-card"><a class="ess-cardlink" href="' + esc(cfg.page) + '">' +
        '<div class="es-card__head"><span class="es-rank">' + (rank < 10 ? '0' + rank : rank) + '</span>' +
          '<div class="es-card__name"><h3 class="es-name">' + esc(r.name) + '</h3>' +
          '<p class="es-where">' + esc([r.region, r.country].filter(Boolean).join(' · ')) + '</p></div></div>' +
        '<p class="ess-total"><b>' + f.snow(m.next7) + '</b><i>' + f.snowUnit + '</i><span>Next 7 days</span></p>' +
        (chips.length ? '<div class="es-chips">' + chips.join('') + '</div>' : '') +
        '<div class="es-stripwrap">' + E.stripSvg(m, f) + '</div>' +
        '</a></li>';
    }

    function draw(list, at) {
      var models = E.parseMet(list, cfg.resorts, Date.now());
      var best = models.filter(function (m) { return !m.missing; })
        .sort(function (a, b) { return b.next7 - a.next7 || a.resort.order - b.resort.order; })
        .slice(0, count);
      if (!best.length) { fail(); return; }
      $('list').innerHTML = best.map(card).join('');
      wrap.classList.remove('ess-loading');
      $('status').textContent = 'Forecast updated ' + stamp(at);
    }

    function fail() {
      wrap.classList.remove('ess-loading');
      wrap.classList.add('ess-failed');
      $('status').textContent = 'The forecast didn’t load just now. The full report links every mountain’s own snow report.';
    }

    // Identical to the page: one GET per mountain, 6 in flight, one retry 1.2 s later, and the set kept for 30 min.
    var urls = cfg.resorts.map(E.metUrl);
    var KEY = 'es-met:' + urls.join('|');
    try {
      var hit = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      if (hit && Date.now() - hit.at < MAX_AGE) { draw(hit.list, hit.at); return; }
    } catch (e) {}
    function one(u, retried) {
      return fetch(u, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { return j ? E.trimMet(j) : null; })
        .catch(function () { return null; })
        .then(function (v) {
          return v || retried ? v : new Promise(function (ok) { setTimeout(ok, 1200); }).then(function () { return one(u, true); });
        });
    }
    function pooled(list, n) {
      var out = new Array(list.length), next = 0;
      function lane() { var i = next++; return i < list.length ? one(list[i]).then(function (v) { out[i] = v; return lane(); }) : null; }
      var lanes = []; for (var k = 0; k < Math.min(n, list.length); k++) lanes.push(lane());
      return Promise.all(lanes).then(function () { return out; });
    }
    pooled(urls, 6).then(function (list) {
      var got = list.filter(Boolean).length;
      if (!got) throw new Error('no forecasts');
      var at = Date.now();
      if (got === list.length) { try { window.sessionStorage.setItem(KEY, JSON.stringify({ at: at, list: list })); } catch (e) {} }
      draw(list, at);
    }).catch(fail);
  }

  function start(wrap) {
    if (wrap.getAttribute('data-es-watch')) return;
    wrap.setAttribute('data-es-watch', '1');
    if (!('IntersectionObserver' in window)) { boot(wrap); return; }
    var io = new IntersectionObserver(function (es) {
      if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); boot(wrap); }
    }, { rootMargin: '600px 0px' });
    io.observe(wrap);
  }

  function all() { Array.prototype.forEach.call(document.querySelectorAll('.edi-snowstrip'), start); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all); else all();
  document.addEventListener('shopify:section:load', all);
})();
